import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ConfirmModal } from '@/components/ui/Modal';
import type { useUnsavedChanges } from '@/lib/useUnsavedChanges';

import { Page, PageHeader } from './Page';
import styles from './EditorPage.module.css';

export function EditorPage({
  title,
  description,
  backTo,
  backLabel,
  actions,
  children,
  unsaved,
  busy = false,
}: {
  title: string;
  description: string;
  backTo: string;
  backLabel: string;
  actions: ReactNode;
  children: ReactNode;
  unsaved: ReturnType<typeof useUnsavedChanges>;
  busy?: boolean;
}) {
  const header = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scrollArea = header.current?.closest('main');
    if (scrollArea) scrollArea.scrollTop = 0;
  }, []);
  return (
    <Page className={styles.page}>
      <div className={styles.header} ref={header}>
        <Link className={styles.back} to={backTo}>
          <ArrowLeft size={14} aria-hidden="true" />
          {backLabel}
        </Link>
        <PageHeader title={title} description={description} actions={actions} />
      </div>
      {children}
      <ConfirmModal
        open={unsaved.blocked}
        title="Leave this editor?"
        body={
          busy
            ? 'Wait for the current save or upload to finish before leaving.'
            : 'Your unsaved changes will be lost.'
        }
        confirmLabel="Discard changes"
        danger
        busy={busy}
        onCancel={unsaved.stay}
        onConfirm={unsaved.discard}
      />
    </Page>
  );
}

export function EditorColumns({
  children,
  settings,
}: {
  children: ReactNode;
  settings: ReactNode;
}) {
  return (
    <div className={styles.columns}>
      <div className={styles.column}>{children}</div>
      <aside className={styles.column} aria-label="Editor settings">
        {settings}
      </aside>
    </div>
  );
}
